import {
  Button,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { codeApi } from "core/api";
import { User } from "core/types/user.types";
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "components/atoms/text";

export type TableData = User & { editMode: boolean };

const Admin: FunctionComponent = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<TableData[]>();
  const [loading, setLoading] = useState(false);
  const [editedUsers, setEditedUsers] = useState<TableData[]>([]);

  const getUsers = useCallback(async () => {
    try {
      const response = await codeApi.getUsers();
      const data: TableData[] = response.map((r: any) => ({
        ...r,
        editMode: false,
      }));
      // Copia i dati in editedUsers inizialmente
      setEditedUsers(data);
      setUsers(data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    getUsers();
    setLoading(false);
  }, [getUsers]);

  const handleFieldChange = (id: string, field: string, value: number) => {
    const updatedEditedUsers: TableData[] = editedUsers.map((user) => {
      if (user.id === id) {
        return {
          ...user,
          billingInfo: user.billingInfo
            ? {
                ...user.billingInfo,
                [field]: value,
              }
            : undefined,
        };
      }
      return user;
    });

    setEditedUsers(updatedEditedUsers);
  };

  const handleEditClick = (id: string) => {
    const userToEdit = editedUsers.find((user) => user.id === id);

    if (userToEdit) {
      const updatedEditedUsers = editedUsers.map((user) =>
        user.id === id ? { ...user, editMode: true } : user
      );
      setEditedUsers(updatedEditedUsers);
    }
  };

  const handleSaveClick = async (id: string) => {
    const userToSave = editedUsers.find((user) => user.id === id);

    if (userToSave) {
      const updatedEditedUsers = editedUsers.map((user) =>
        user.id === id ? { ...user, editMode: false } : user
      );
      setEditedUsers(updatedEditedUsers);

      setLoading(true);

      try {
        const response = await codeApi.saveUser({
          id: userToSave.id,
          level: userToSave.billingInfo?.level ?? "",
          report_count: userToSave.billingInfo?.reportCount ?? 0,
          searches_count: userToSave.billingInfo?.searchesCount ?? 0,
          tokens_count: userToSave.billingInfo?.tokensCount ?? 0,
        });
        if (response.status === 200) {
          getUsers();
        }
      } catch (err) {
        console.error(err);
      }

      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          paddingTop: "200px",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress thickness={8} size={"8rem"} color="secondary" />
      </div>
    );
  }

  const handleCancelClick = (id: string) => {
    if (users) {
      const userToCancel = users?.find((user) => user.id === id);

      if (userToCancel) {
        const updatedEditedUsers = users.map((user) =>
          user.id === id ? { ...userToCancel, editMode: false } : user
        );
        setEditedUsers(updatedEditedUsers);
      }
    }
  };

  return (
    <div style={{}}>
      <Text fontSize={"24px"} fontWeight={"700"} textAlign={"center"}>
        {t("admin.title")}
      </Text>
      <Paper
        sx={{
          width: "100%",
          overflow: "hidden",
          borderRadius: "10px",
        }}
      >
        <TableContainer sx={{ maxHeight: "calc(100vh - 220px)", overflow: "auto" }}>
          <Table sx={{ width: "100%" }} size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{t("admin.table.firstName")}</TableCell>
                <TableCell>{t("admin.table.lastName")}</TableCell>
                <TableCell>{t("admin.table.email")}</TableCell>
                <TableCell>{t("admin.table.available-searches")}</TableCell>
                <TableCell>{t("admin.table.remaining-reports")}</TableCell>
                <TableCell>{t("admin.table.remaining-token")}</TableCell>
                <TableCell>Rimozioni (rim./mese)</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody
              sx={{
                "& .MuiTableCell-root": {
                  padding: "10px",
                  fontSize: "14px",
                },
              }}
            >
              {editedUsers?.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.firstName}</TableCell>
                  <TableCell>{user.lastName}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {user.editMode ? (
                      <TextField
                        type="number"
                        size="small"
                        variant="standard"
                        value={user.billingInfo?.searchesCount}
                        onChange={(e) =>
                          handleFieldChange(
                            user.id,
                            "searchesCount",
                            parseInt(e.target.value, 10)
                          )
                        }
                      />
                    ) : (
                      user.billingInfo?.searchesCount
                    )}
                  </TableCell>
                  <TableCell>
                    {user.editMode ? (
                      <TextField
                        type="number"
                        size="small"
                        variant="standard"
                        value={user.billingInfo?.reportCount}
                        onChange={(e) =>
                          handleFieldChange(
                            user.id,
                            "reportCount",
                            parseInt(e.target.value, 10)
                          )
                        }
                      />
                    ) : (
                      user.billingInfo?.reportCount
                    )}
                  </TableCell>
                  <TableCell>
                    {user.editMode ? (
                      <TextField
                        type="number"
                        size="small"
                        variant="standard"
                        value={user.billingInfo?.tokensCount}
                        onChange={(e) =>
                          handleFieldChange(
                            user.id,
                            "tokensCount",
                            parseInt(e.target.value, 10)
                          )
                        }
                      />
                    ) : (
                      user.billingInfo?.tokensCount
                    )}
                  </TableCell>
                  <TableCell>
                    {user.billingInfo?.removalsCap != null
                      ? `${user.billingInfo?.removalsRemaining ?? 0} / ${user.billingInfo?.removalsCap}`
                      : "-"}
                  </TableCell>
                  <TableCell sx={{ minWidth: "200px" }}>
                    {user.editMode ? (
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Button
                            fullWidth
                            variant="contained"
                            onClick={() => handleSaveClick(user.id)}
                          >
                            {t("admin.table.save")}
                          </Button>
                        </Grid>
                        <Grid item xs={6}>
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => handleCancelClick(user.id)}
                          >
                            {t("admin.table.undo")}
                          </Button>
                        </Grid>
                      </Grid>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={() => handleEditClick(user.id)}
                      >
                        {t("admin.table.edit")}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
};

export default Admin;
